import React, { useMemo, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
//Import Breadcrumb
import { Container } from "reactstrap";
import "react-toastify/dist/ReactToastify.css";

import { Search, Upload } from "lucide-react";

import Paginate from "../../components/shared/paginate";
import { toast } from "react-toastify";
import {
  DownloadCSV,
  FetchReservations,
  SearchRecords,
} from "../../apis/Reservation";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import { GenerateShortToken } from "../../apis/Auth";

const Reservation = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.MANAGE_RESERVATIONS);
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  // const [users, setUsers] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [dataInCSV, setDataInCSV] = useState("");
  const [keyword, setKeyword] = useState("");
  const limit = "10";
  const [shortToken, setShortToken] = useState("");
  const tokenTimestampRef = useRef(0);
  const generatingPromiseRef = useRef(null);
  const TOKEN_TTL_MS = 3 * 60 * 1000;

  const getValidShortToken = async () => {
    if (
      shortToken &&
      tokenTimestampRef.current &&
      Date.now() - tokenTimestampRef.current < TOKEN_TTL_MS
    ) {
      return shortToken;
    }
    if (generatingPromiseRef.current) {
      try {
        await generatingPromiseRef.current;
        return shortToken;
      } catch {
        return "";
      }
    }
    generatingPromiseRef.current = (async () => {
      try {
        const response = await GenerateShortToken();
        const token = response.data?.token || "";
        setShortToken(token);
        tokenTimestampRef.current = Date.now();
        return token;
      } catch (err) {
        toast.error("Failed to generate short token");
        return "";
      } finally {
        generatingPromiseRef.current = null;
      }
    })();
    const newToken = await generatingPromiseRef.current;
    return newToken || "";
  };

  const fetchReservations = async () => {
    const response = await FetchReservations(currentPage, limit); // Assuming FetchHouseTypes is the correct function from your API
    setFilteredReservations(response?.data?.reservations);
    setTotalPages(response?.data?.totalPages);
  };

  const downloadCSV = () => {
    DownloadCSV().then((res) => {
      setDataInCSV(res);
    });
  };

  useEffect(() => {
    downloadCSV();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchReservations = async () => {
    try {
      const response = await SearchRecords(keyword, currentPage, limit);
      if (response.success) {
        setFilteredReservations(response.data.reservations);
        setTotalPages(response.data.totalPages);
      } else {
        toast.error("Failed to search reservations. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to search reservations. Please try again later.");
    }
  };

  useEffect(() => {
    if (keyword) {
      searchReservations();
    } else fetchReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, currentPage]);

  useEffect(() => {
    if (keyword) {
      setCurrentPage("1");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;
    const check = new Date(checkIn);
    const checkout = new Date(checkOut);
    const diffTime = Math.abs(checkout - check);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const columns = useMemo(
    () => [
      "Code",
      "Guest Name",
      "Host",
      "Property",
      "Check-in / Check-out",
      "Guests",
      "Nights",
      "Booking Status",
      "Payment Status",
      "Total",
      "Coupon",
      "Fees",
      "Actions",
    ],
    [],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6 flex flex-col gap-6 ">
            <h1 className="text-2xl dark:text-[#c7d8eb]">
              Manage Reservations
            </h1>
            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white">
              <div className="border-x border-t lg:border-none border-gray-500 dark:border-white rounded-[6px_6px_0px_0px] w-full flex flex-col lg:flex-row justify-between gap-4 p-4">
                <div className="relative border max-w-[400px] border-gray-500 dark:border-white rounded-md w-full">
                  <Search
                    size={16}
                    className="absolute left-2 bottom-1/2 translate-y-1/2"
                  />
                  <input
                    className="pl-8 pr-6 py-2 text-base rounded-md w-full dark:bg-darkBg2"
                    type="search"
                    placeholder="Search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                </div>
                <div className="flex lg:justify-self-end">
                  <a
                    href={`data:text/csv;charset=utf-8,${escape(dataInCSV)}`}
                    download="listings-list.csv"
                  >
                    <div className="flex items-center gap-2">
                      Export data into CSV
                      <div className="shrink-0 bg-white p-1">
                        <Upload className="" size={16} />
                      </div>
                    </div>
                  </a>
                </div>
              </div>
              <div className="overflow-x-auto w-full">
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className={`${
                        index === 0
                          ? "w-[8%]"
                          : index === 1
                            ? "w-[9%]"
                            : index === 2
                              ? "w-[9%]"
                              : index === 3
                                ? "w-[8%]"
                                : index === 4
                                  ? "w-[9%]"
                                  : index === 5
                                    ? "w-[5%]"
                                    : index === 6
                                      ? "w-[5%]"
                                      : index === 7
                                        ? "w-[7%]"
                                        : index === 8
                                          ? "w-[7%]"
                                          : index === 9
                                            ? "w-[6%]"
                                            : index === 10
                                              ? "w-[8%]"
                                              : index === 11
                                                ? "w-[8%]"
                                                : "w-[12%]"
                      } min-w-[130px] content-center border-y border-gray-500 dark:border-white break-words py-2 text-center font-semibold text-xs`}
                    >
                      {column}
                    </div>
                  ))}
                </div>
                {filteredReservations &&
                  filteredReservations.map((reservation, index) => (
                    <>
                      <div
                        key={`reservation-desktop-${index}`}
                        className="hidden lg:flex w-full"
                      >
                        <div className="w-[8%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          #{reservation.code}
                        </div>
                        <div className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          <a
                            href={`${process.env.REACT_APP_CLIENTSIDE_URL}/profile/${reservation.guest?._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-secondaryColor hover:underline"
                          >
                            {reservation.guest?.firstName}{" "}
                            {reservation.guest?.lastName}
                          </a>
                        </div>
                        <div className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          <a
                            href={`${process.env.REACT_APP_CLIENTSIDE_URL}/profile/${reservation.host?._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-secondaryColor hover:underline"
                          >
                            {reservation.host?.firstName}{" "}
                            {reservation.host?.lastName}
                          </a>
                        </div>
                        <div className="w-[8%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          <button
                            type="button"
                            className="text-secondaryColor hover:underline"
                            onClick={async () => {
                              const token = await getValidShortToken();
                              if (token && reservation.property?._id) {
                                const url = `${process.env.REACT_APP_CLIENTSIDE_URL}/rooms/${reservation.property._id}?token=${token}`;
                                window.open(url, "_blank");
                              }
                            }}
                          >
                            {reservation.property?.title}
                          </button>
                        </div>
                        <div className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          <div className="text-xs">
                            {reservation.checkIn?.toString().slice(0, 10)}
                          </div>
                          <div className="text-xs">
                            {reservation.checkOut?.toString().slice(0, 10)}
                          </div>
                        </div>
                        <div className="w-[5%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {reservation.guests}
                        </div>
                        <div className="w-[5%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs font-semibold">
                          {calculateNights(
                            reservation.checkIn,
                            reservation.checkOut,
                          )}
                        </div>
                        <div className="w-[7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              reservation.reservationState === "pending"
                                ? "bg-yellow-500 text-white"
                                : reservation.reservationState === "approved"
                                  ? "bg-green-500 text-white"
                                  : reservation.reservationState === "declined"
                                    ? "bg-red-500 text-white"
                                    : reservation.reservationState ===
                                        "completed"
                                      ? "bg-blue-500 text-white"
                                      : "bg-gray-500 text-white"
                            }`}
                          >
                            {reservation.reservationState}
                          </span>
                        </div>
                        <div className="w-[7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              reservation.paymentState === "pending"
                                ? "bg-yellow-500 text-white"
                                : reservation.paymentState === "completed"
                                  ? "bg-green-500 text-white"
                                  : "bg-red-500 text-white"
                            }`}
                          >
                            {reservation.paymentState}
                          </span>
                        </div>
                        <div className="w-[6%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs font-semibold">
                          ${reservation.totalPrice}
                        </div>
                        <div className="w-[8%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {reservation.coupon?.discountAmount ? (
                            <span className="text-red-600 font-semibold">
                              -${reservation.coupon.discountAmount}
                            </span>
                          ) : (
                            "-"
                          )}
                        </div>
                        <div className="w-[8%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {reservation.fees && reservation.fees.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {reservation.fees.map((fee, feeIndex) => (
                                <div
                                  key={feeIndex}
                                  className="text-xs whitespace-nowrap"
                                >
                                  <span className="font-semibold">
                                    {fee.feeType}
                                  </span>
                                  <br />
                                  <span className="text-green-600">
                                    ${fee.amount}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            "-"
                          )}
                        </div>
                        <div className="w-[12%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <div className="flex justify-center gap-2 flex-wrap">
                            <Link
                              className="text-secondaryColor text-xs hover:underline"
                              to={`/reservation/${reservation._id}`}
                            >
                              View
                            </Link>
                            {/* {canManage && (
                              <Link
                                className="text-secondaryColor text-xs hover:underline"
                                to={`/reservation/manage/${reservation._id}`}
                              >
                                Manage
                              </Link>
                            )} */}
                            {canManage &&
                              reservation.reservationState === "approved" && (
                                <Link
                                  className="text-red-500 text-xs hover:underline"
                                  to={`/reservation/cancel/${reservation._id}`}
                                  target="_blank"
                                >
                                  Cancel
                                </Link>
                              )}
                          </div>
                        </div>
                      </div>
                      <div
                        key={`reservation-mobile-${index}`}
                        className="block lg:hidden mb-6 border-x border-y border-gray-500 dark:border-white"
                      >
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Code</div>
                          <div className="font-semibold">
                            #{reservation.code}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold ">Guest</div>
                          <a
                            href={`${process.env.REACT_APP_CLIENTSIDE_URL}/profile/${reservation.guest?._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-secondaryColor text-sm hover:underline"
                          >
                            {reservation.guest?.firstName}{" "}
                            {reservation.guest?.lastName}
                          </a>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Host</div>
                          <a
                            href={`${process.env.REACT_APP_CLIENTSIDE_URL}/profile/${reservation.host?._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-secondaryColor text-sm hover:underline"
                          >
                            {reservation.host?.firstName}{" "}
                            {reservation.host?.lastName}
                          </a>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Property</div>
                          <button
                            type="button"
                            className="text-secondaryColor text-sm hover:underline"
                            onClick={async () => {
                              const token = await getValidShortToken();
                              if (token && reservation.property?._id) {
                                const url = `${process.env.REACT_APP_CLIENTSIDE_URL}/rooms/${reservation.property._id}?token=${token}`;
                                window.open(url, "_blank");
                              }
                            }}
                          >
                            {reservation.property?.title}
                          </button>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Check-in</div>
                          <div className="text-sm">
                            {reservation.checkIn?.toString().slice(0, 10)}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Check-out</div>
                          <div className="text-sm">
                            {reservation.checkOut?.toString().slice(0, 10)}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Guests</div>
                          <div className="text-sm">{reservation.guests}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Nights</div>
                          <div className="text-sm font-semibold">
                            {calculateNights(
                              reservation.checkIn,
                              reservation.checkOut,
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Booking Status</div>
                          <div>
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ${
                                reservation.reservationState === "pending"
                                  ? "bg-yellow-500 text-white"
                                  : reservation.reservationState === "approved"
                                    ? "bg-green-500 text-white"
                                    : reservation.reservationState ===
                                        "declined"
                                      ? "bg-red-500 text-white"
                                      : reservation.reservationState ===
                                          "completed"
                                        ? "bg-blue-500 text-white"
                                        : "bg-gray-500 text-white"
                              }`}
                            >
                              {reservation.reservationState}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Payment Status</div>
                          <div>
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ${
                                reservation.paymentState === "pending"
                                  ? "bg-yellow-500 text-white"
                                  : reservation.paymentState === "completed"
                                    ? "bg-green-500 text-white"
                                    : "bg-red-500 text-white"
                              }`}
                            >
                              {reservation.paymentState}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Total</div>
                          <div className="font-semibold">
                            ${reservation.totalPrice}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Coupon Discount</div>
                          <div className="text-sm">
                            {reservation.coupon?.discountAmount ? (
                              <span className="text-red-600 font-semibold">
                                -${reservation.coupon.discountAmount}
                              </span>
                            ) : (
                              "-"
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Fees</div>
                          <div className="text-sm">
                            {reservation.fees && reservation.fees.length > 0 ? (
                              <div className="flex flex-col gap-1 items-end">
                                {reservation.fees.map((fee, feeIndex) => (
                                  <div key={feeIndex} className="text-xs">
                                    <span className="font-semibold">
                                      {fee.feeType}
                                    </span>
                                    {" - "}
                                    <span className="text-green-600 font-semibold">
                                      ${fee.amount}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              "-"
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2">
                          <div className="font-bold">Actions</div>
                          <div className="flex gap-2">
                            <Link
                              className="text-secondaryColor text-sm hover:underline"
                              to={`/reservation/${reservation._id}`}
                            >
                              View
                            </Link>
                            {/* {canManage && (
                              <Link
                                className="text-secondaryColor text-sm hover:underline"
                                to={`/reservation/manage/${reservation._id}`}
                              >
                                Manage
                              </Link>
                            )} */}
                            {canManage &&
                              reservation.reservationState === "approved" && (
                                <Link
                                  className="text-red-500 text-sm hover:underline"
                                  to={`/reservation/cancel/${reservation._id}`}
                                  target="_blank"
                                >
                                  Cancel
                                </Link>
                              )}
                          </div>
                        </div>
                      </div>
                    </>
                  ))}
              </div>
            </div>
            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Reservation;
